/**
 * 営業日カレンダーの入力欄について「**何を出すか**」だけを決める（M5 / §3.3）。
 *
 * ★このファイルは **DOM を触らない**（`targets-view.ts` と同じ理由）。
 *   判断を描画関数の中に書くと、その判断を壊しても `tsc` もテストも落ちない。
 *
 * ## ★ここが担っている唯一の判断：「下書き」と「登録済み」を分ける
 *
 * 未登録の日にもサーバは定休日パラメータから作った案（`registered: false`）を入れて返す。
 * ★**案と事実を同じ見た目で出すと、見ただけで営業日数が決まったと錯覚する。**
 * 営業日数は `business_calendar` の行から数える約束（`docs/02` §1.1 / D-06）で、
 * 1日でも行が無い月は `findMonthCalendar` が失敗する＝**日商平均も固定費の配賦も出ない。**
 * 現行Excel の 23/30/22/30 は「誰かが決めた数」がそのまま計算に乗った結果だった。
 */
import type { CalendarDay, CalendarDraft } from "./api.js";

const WEEKDAY = ["日", "月", "火", "水", "木", "金", "土"] as const;

/** 未登録の日に添える言葉。★登録済みの日には出さない */
export const DRAFT_NOTE = "未登録（定休日の設定から作った案）";

export interface CalendarRowView {
  businessDate: string;
  /** `01(月)` の形。★月は欄の外（見出し）に1度だけ出す */
  label: string;
  isOpen: boolean;
  /**
   * ★祝日の扱いは Q-18 未回答。**画面から入力させず、保存されている値をそのまま往復させる。**
   *   休業にするのは `isOpen` を外す操作で、こちらは触らない。
   */
  isHoliday: boolean;
  closeReason: string;
  /** ★`false` は下書き。`note` が付く */
  registered: boolean;
  note: string | null;
}

export function calendarRows(days: CalendarDay[]): CalendarRowView[] {
  return days.map((d) => {
    const weekday = WEEKDAY[new Date(`${d.businessDate}T00:00:00Z`).getUTCDay()] ?? "";
    return {
      businessDate: d.businessDate,
      label: `${d.businessDate.slice(8, 10)}(${weekday})`,
      isOpen: d.isOpen,
      isHoliday: d.isHoliday,
      closeReason: d.closeReason ?? "",
      registered: d.registered,
      note: d.registered ? null : DRAFT_NOTE,
    };
  });
}

/**
 * 見出しの下に出す1行。
 *
 * ★**「全部入っている」ときも黙らない**（`targets-view.ts` の `dailyTargetsSummary` と同じ約束）。
 * ★★**未登録が1日でもあれば営業日数を言わない。** 下書きを数えて「26日」と出すと、
 *   保存していないのに営業日数が決まったように読める——現行Excel の手入力と同じ形になる。
 */
export function calendarSummary(draft: CalendarDraft): string {
  const rows = draft.days;
  if (rows.length === 0) return "この月の日付が取得できませんでした";
  const unregistered = rows.filter((r) => !r.registered).length;
  const open = rows.filter((r) => r.isOpen).length;
  if (unregistered === 0) {
    return (
      `${rows.length}日ぶん登録済みです。営業日は ${open}日です` +
      `（客数の目標達成率は売上タブに出ます）`
    );
  }
  /*
   * ★**開発の識別子（C-09）を利用者向けの文言に出さない**（2026-09-04・spec が差し戻し）。
   *   読むのは店長とオーナーで、記号を見ても何も分からない。
   *   「括弧の中身を消しても文が成立するか」で判定する——成立するなら開発者向け。
   *   対応関係はこのコメントに残せば足りる（C-09 = 営業日数と配賦の除数の突き合わせ）。
   */
  return (
    `${rows.length}日のうち ${unregistered}日が未登録です。` +
    `保存するまで営業日数は確定しません（日商平均・固定費の配賦・自動検算が出せません）`
  );
}
